import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Ship,
  Plane,
  Truck,
  ShieldCheck,
  Warehouse,
  PackageCheck,
  Globe2,
  Package,
  ArrowRight,
} from "lucide-react";
import Reveal from "../components/site/Reveal";
import Seo, { SITE_URL } from "../components/site/Seo";
import {
  IMAGES,
  LOGOS,
  LOGISTICS_SERVICES,
  LOGISTICS_VALUES,
} from "../lib/site-data";

const ICONS = [Ship, Plane, Truck, ShieldCheck, Warehouse, PackageCheck, Package, Globe2];

const LOGISTICS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Ocean Oasis Logistics",
  url: `${SITE_URL}/logistics`,
  description:
    "Somalia-based international logistics and supply chain company — ocean freight, air freight, land transportation, customs clearance, warehousing and distribution from Mogadishu.",
  telephone: "+252610424241",
  email: "info@oceanoasis.llc",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Sundus Plaza, Bondhere District",
    addressLocality: "Mogadishu",
    addressCountry: "SO",
  },
  areaServed: "Worldwide",
  parentOrganization: { "@type": "Organization", name: "Ocean Oasis", url: SITE_URL },
};

export default function Logistics() {
  return (
    <main data-testid="page-logistics" className="overflow-hidden">
      <Seo
        title="Ocean Oasis Logistics | Cargo, Freight & Supply Chain — Mogadishu, Somalia"
        description="Integrated logistics from Mogadishu: ocean & air freight, land transportation, customs clearance, warehousing and distribution connecting Somalia with global trade routes."
        path="/logistics"
        jsonLd={LOGISTICS_JSONLD}
      />
      {/* HERO — editorial split: cream-left panel + image-right panel */}
      <section className="relative bg-[#fdf9f2]">
        <div className="grid lg:grid-cols-12 min-h-[100vh]">
          {/* Cream panel with logo */}
          <div className="lg:col-span-6 flex items-center pt-32 pb-16 lg:pb-24 px-6 md:px-12 lg:px-16 xl:px-24">
            <div className="max-w-xl w-full">
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="font-label text-[#f59e0b] mb-8 flex items-center gap-3"
              >
                <span className="w-10 h-px bg-[#f59e0b]" />
                Ocean Oasis · Division 01
              </motion.p>
              <motion.img
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                src={LOGOS.logistics}
                alt="Ocean Oasis Logistics logo"
                className="w-full max-w-md h-auto object-contain mix-blend-multiply"
              />
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="font-display text-[#0a3d7a] leading-[0.95] mt-8"
                style={{ fontSize: "clamp(2.2rem, 4.5vw, 4rem)" }}
              >
                From land to <em className="italic text-[#f59e0b]">sea</em> to sky.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="font-body text-[#0a3d7a]/80 text-base md:text-lg leading-relaxed mt-6"
              >
                Integrated international logistics and supply chain solutions —
                engineered out of Mogadishu to move cargo, business and
                humanitarian operations between Somalia and the rest of the
                world.
              </motion.p>
            </div>
          </div>

          {/* Image panel */}
          <div className="lg:col-span-6 relative min-h-[55vh] lg:min-h-full overflow-hidden bg-[#0a3d7a]">
            <img
              src={IMAGES.logisticsHero}
              alt="Cargo ship at sea"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#0a3d7a]/10 to-[#0a3d7a]/30" />
          </div>
        </div>
      </section>

      {/* MISSION BAR */}
      <div className="marquee-strip">
        <Marquee speed={38} gradient={false} pauseOnHover>
          {[
            "Connecting Somalia to the World",
            "From Land to Sea to Sky",
            "Trusted Cargo Solutions Worldwide",
            "Delivering Beyond Borders",
            "Your Gateway to Global Trade",
            "Fast · Reliable · Global",
          ].map((t, i) => (
            <span
              key={i}
              className="font-display italic text-2xl md:text-3xl mx-10 text-[#faf6f0]"
            >
              {t} <span className="text-[#f59e0b] mx-3">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* INTRO */}
      <section className="py-24 md:py-32 bg-[#faf6f0]">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-label text-[#f59e0b]">Our Mission</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.02] text-[#0a3d7a]">
                  A logistics bridge between Somalia and the world.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 space-y-6">
              <Reveal delay={0.1}>
                <p className="font-body text-lg text-[#0a3d7a]/85 leading-relaxed">
                  Ocean Oasis Logistics is a Somalia-based international
                  logistics and supply chain solutions company, dedicated to
                  providing reliable, efficient and globally connected
                  transportation services to businesses, humanitarian
                  organizations, government institutions, importers, exporters
                  and international partners.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="font-body text-base text-[#0a3d7a]/70 leading-relaxed">
                  We specialise in integrated logistics operations covering
                  <em className="italic font-display text-[#0a3d7a] text-lg"> land, ocean and air transportation</em> —
                  built on responsive coordination, professional expertise and
                  a deep understanding of Somalia's logistics environment.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-24 md:py-32 bg-[#fdf9f2]">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
              <div>
                <p className="font-label text-[#f59e0b]">Services</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#0a3d7a]">
                  A full suite of <em className="italic">cargo solutions.</em>
                </h2>
              </div>
              <p className="font-body text-base text-[#0a3d7a]/70 max-w-md">
                Whatever the cargo, destination or urgency — our integrated
                operations move it safely, lawfully and on time.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[rgba(10,61,122,0.15)] border border-[rgba(10,61,122,0.15)]">
            {LOGISTICS_SERVICES.map((s, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <Reveal
                  key={s.title}
                  delay={(i % 4) * 0.08}
                  className="group bg-[#fdf9f2] hover:bg-[#0a3d7a] transition-colors duration-500 p-8 md:p-10 cursor-default"
                >
                  <Icon
                    size={28}
                    className="text-[#f59e0b] mb-6"
                    strokeWidth={1.5}
                  />
                  <p className="font-label text-[#0a3d7a]/60 group-hover:text-[#faf6f0]/70 transition-colors mb-3">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl text-[#0a3d7a] group-hover:text-[#faf6f0] transition-colors mb-3 leading-tight">
                    {s.title}
                  </h3>
                  <p className="font-body text-sm text-[#0a3d7a]/70 group-hover:text-[#faf6f0]/80 transition-colors leading-relaxed">
                    {s.desc}
                  </p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DUAL IMAGE EDITORIAL */}
      <section className="py-24 md:py-32 bg-[#0a3d7a] text-[#faf6f0]">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-center mb-16">
            <div className="lg:col-span-6">
              <Reveal>
                <p className="font-label text-[#f59e0b]">Sectors We Serve</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.02]">
                  Wherever Somalia <em className="italic">trades, builds</em> or heals — we move with you.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-5 lg:col-start-8">
              <Reveal delay={0.1}>
                <p className="font-body text-[#faf6f0]/80 text-base leading-relaxed">
                  We support commercial businesses, humanitarian organizations,
                  government institutions, construction and infrastructure
                  projects, oil and energy operations, healthcare and medical
                  supply chains, retail, manufacturing, agriculture, aviation
                  and maritime sectors.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
            <Reveal className="img-zoom aspect-[4/3] overflow-hidden">
              <img
                src={IMAGES.logisticsWarehouse}
                alt="Ocean Oasis Logistics warehouse and storage operations"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </Reveal>
            <Reveal delay={0.1} className="img-zoom aspect-[4/3] overflow-hidden">
              <img
                src={IMAGES.truck}
                alt="Ocean Oasis Logistics land transportation cargo truck"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALUES STRIP */}
      <section className="py-24 md:py-32 bg-[#faf6f0]">
        <div className="container-x">
          <Reveal>
            <p className="font-label text-[#f59e0b]">Our Values</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#0a3d7a] mb-12 leading-tight">
              The principles that move every shipment.
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {LOGISTICS_VALUES.map((v, i) => (
              <Reveal
                key={v}
                delay={i * 0.05}
                className="border border-[rgba(10,61,122,0.15)] p-6 md:p-8 hover:bg-[#0a3d7a] hover:text-[#faf6f0] transition-colors duration-500 group"
              >
                <p className="font-label text-[#f59e0b] mb-3">
                  0{i + 1 > 9 ? i + 1 : i + 1}
                </p>
                <p className="font-display text-xl md:text-2xl text-[#0a3d7a] group-hover:text-[#faf6f0] transition-colors leading-snug">
                  {v}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#fdf9f2]">
        <div className="container-x">
          <Reveal>
            <div className="max-w-3xl">
              <p className="font-label text-[#f59e0b] mb-5">Get in Touch</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0a3d7a] leading-[1.02] mb-8">
                Ready to <em className="italic">move cargo</em> with people who know Somalia?
              </h2>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" data-testid="logistics-cta-contact" className="btn-primary">
                  Talk to Logistics <ArrowRight size={16} />
                </Link>
                <Link to="/" data-testid="logistics-cta-home" className="btn-outline">
                  Back to Group
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
